import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Canvas } from '@react-three/fiber';
import {
  Html,
  useProgress,
  CameraControls,
  PerspectiveCamera,
  OrthographicCamera,
  GizmoHelper,
  GizmoViewport,
} from '@react-three/drei';
import * as THREE from 'three';
import {
  annotationState,
  rotationCCWState,
  rotationCWState
} from 'features/three-view/state/threeAtoms';
import { ThreeViewer } from './ThreeViewer';
import { ViewLabel } from './ViewLabel';
import { annotationClick } from '../utils/annotationClick';
import { rotationClick } from '../utils/rotationClick';
import { useActiveCamera } from '../hooks/useActiveCamera';
import { useViewModeSync } from '../hooks/useViewModeSync';
import { projectionModeState } from '../state/viewModeAtoms';

interface Props {
  width: number;
  height: number;
}

const Loader = () => {
  const { progress } = useProgress();
  return (
    <Html center>
      <progress value={progress} max="100" />
    </Html>
  );
};

export const ThreeViewPanel: React.FC<Props> = ({ width, height }) => {
  const cameraRef = useRef<CameraControls>(null!);
  const [perspCam, setPerspCam] = useState<THREE.PerspectiveCamera | null>(null);
  const [orthoCam, setOrthoCam] = useState<THREE.OrthographicCamera | null>(null);
  const annotation = useRecoilValue(annotationState);
  const rotationCW = useRecoilValue(rotationCWState);
  const rotationCCW = useRecoilValue(rotationCCWState);
  const projectionMode = useRecoilValue(projectionModeState);

  const activeCamera = useActiveCamera({ perspCam, orthoCam });
  useViewModeSync({ cameraRef, perspCam, orthoCam, canvasHeight: height });

  useEffect(() => {
    annotationClick(cameraRef, annotation);
  }, [annotation]);

  useEffect(() => {
    rotationClick(cameraRef, true);
  }, [rotationCW]);

  useEffect(() => {
    rotationClick(cameraRef, false);
  }, [rotationCCW]);

  return (
    <div style={{ width: '100%', height: '10%', position: 'relative' }}>
      <ViewLabel />
      <Canvas style={{ width, height }}>
        {/* drei の makeDefault で useThree() の camera を切替。
            未設定だと Canvas の暗黙 default camera が使われ、CameraControls 制御対象と不一致になる。 */}
        <PerspectiveCamera
          ref={setPerspCam}
          makeDefault={projectionMode === 'perspective'}
          position={[0, -3, 2]}
          fov={50}
        />
        <OrthographicCamera
          ref={setOrthoCam}
          makeDefault={projectionMode === 'orthographic'}
          position={[0, -3, 2]}
          zoom={100}
        />
        {activeCamera && (
          <CameraControls ref={cameraRef} camera={activeCamera} makeDefault />
        )}
        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
          <GizmoViewport axisColors={['#ff3653', '#8adb00', '#2c8fff']} labelColor="white" />
        </GizmoHelper>
        <React.Suspense fallback={<Loader />}>
          <ThreeViewer />
        </React.Suspense>
      </Canvas>
    </div>
  );
};
